<template>
  <div class="asc-fleet-graph--container">
    <asc-spinner-wrapper :loading="loading">
      <div class="asc-fleet-graph--chart">
        <v-row no-gutters>
          <v-col sm="1" md="6" class="mb-1">
            <asc-date-picker
              id="fleet-graph-dates"
              key="fleet-graph-dates"
              v-model="savedDates"
              label="Dates"
              :max-days="365"
              :show-fixed-options="true"
              :hide-options-with-id="[1, 6, 7]"
              :default-option-id="dates.defaultOptionId"
              :has-icon="true"
              auto-re-position
              hide-details
              drop-down
              class="fleet-graph-dates"
              @input:id="handleInput"
            ></asc-date-picker>
          </v-col>
          <v-col v-if="!isEmpty" sm="12" md="6" class="asc-fleet-graph--filter">
            <asc-sort-box-chart-button
              class="asc-fleet-graph--sort-button"
              :sort="sortItem"
              :on-sort="handleSort"
            ></asc-sort-box-chart-button>
          </v-col>
        </v-row>
        <asc-box-chart
          v-if="!isEmpty"
          :options="getUpdatedChartOptions"
        ></asc-box-chart>
      </div>
      <asc-no-data
        v-if="isEmpty"
        class="asc-fleet-graph--no-data"
      ></asc-no-data>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import AscBoxChart from './AscBoxChart'
import { AscDataDefinitionMixin } from '@/components/mixins'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscSortBoxChartButton from '@/components/atoms/AscSortBoxChartButton'
import AscDatePicker from '@/components/atoms/AscDatePicker'
import AscNoData from '@/components/atoms/AscNoData'
import { getReportExportMaxDate } from '@/utilities/dates'
import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscFleetGraph',

  components: {
    AscDatePicker,
    AscSpinnerWrapper,
    AscBoxChart,
    AscSortBoxChartButton,
    AscNoData,
  },

  mixins: [AscDataDefinitionMixin],

  props: {
    dates: {
      type: Object,
      default: () => {},
    },
    onDateChange: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      chartOptions: null,
      sortItem: {
        sortField: 'pct50',
        sortFieldName: 'Med',
        sortAsc: false,
      },
      defaultOptions: {
        chart: {
          type: 'boxplot',
          inverted: true,
          spacing: 50,
        },
        legend: {
          enabled: false,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
        series: [],
      },
      savedDates: this.dates,
      maxDate: getReportExportMaxDate(),
    }
  },

  computed: {
    getUpdatedChartOptions() {
      if (this.chartOptions) {
        return this.chartOptions
      }
      this.updateChartOptions()
      return this.chartOptions
    },

    isEmpty() {
      return (
        !this.loading &&
        (this.definition === undefined ||
          this.definition.parameterDefinition === undefined ||
          this.definition.fleetValues === undefined ||
          (this.definition.fleetValues.primaryPlots.length === 0 &&
            this.definition.fleetValues.secondaryPlots.length === 0))
      )
    },

    getChartOptions() {
      if (!this.isEmpty) {
        const fleetGraphData = this.getFleetGraphData()
        if (this.definition && fleetGraphData) {
          const chart = this.buildChart
          const title = this.buildTitle
          const series = this.buildSeries
          const xAxis = this.buildXAxis
          const yAxis = this.buildYAxis
          return {
            ...this.defaultOptions,
            ...chart,
            ...title,
            ...xAxis,
            ...yAxis,
            ...series,
          }
        }
      }
      return this.defaultOptions
    },

    buildChart() {
      const data = this.getFleetGraphData()
      const height = data.values.length * 36 + 100
      return {
        chart: {
          ...this.defaultOptions.chart,
          height: height < 500 ? 500 : height,
        },
      }
    },

    buildTitle() {
      return {
        title: {
          text: this.definition.parameterName,
        },
      }
    },

    buildXAxis() {
      const data = this.getFleetGraphData()
      const categories = data.values.map(item => {
        return item.name
      })
      return {
        xAxis: {
          categories,
          title: {
            text: '',
          },
        },
      }
    },

    buildYAxis() {
      let units = ''
      if (
        this.definition &&
        this.definition.thresholds &&
        this.definition.thresholds.length > 0
      ) {
        const first = this.definition.thresholds[0]
        units = first.units
      }
      const yAxisOptions = {
        yAxis: [
          {
            title: {
              text: units,
            },
          },
          {
            linkedTo: 0,
            opposite: true,
            title: {
              text: units,
            },
          },
        ],
      }

      return yAxisOptions
    },

    buildSeries() {
      const data = this.getFleetGraphData()
      const series = data.values.map(item => {
        return {
          name: item.name,
          pointWidth: 12,
          medianWidth: 4,
          whiskerLength: 12,
          whiskerWidth: 4,
          data: item.data,
          color:
            item.name?.toLowerCase() ===
            this.$route.params.tailNumber?.toLowerCase()
              ? '#2D6BFF'
              : variables.accent500,
          tooltip: {
            headerFormat: '<em>Statistics</em><br/>',
            valueDecimals: 2,
            pointFormatter: function () {
              return (
                'Maximum: ' +
                this.high.toFixed(2) +
                '<br/>' +
                'Upper quartile: ' +
                this.q3.toFixed(2) +
                '<br/>' +
                'Median: ' +
                this.median.toFixed(2) +
                '<br/>' +
                'Lower quartile: ' +
                this.q1.toFixed(2) +
                '<br/>' +
                'Minimum: ' +
                this.low.toFixed(2) +
                '<br/>'
              )
            },
          },
        }
      })

      return {
        series: series,
      }
    },
  },

  watch: {
    dates: {
      immediate: true,
      deep: true,
      handler(newData) {
        this.dates = newData
        this.updateChartOptions(this.getChartOptions)
      },
    },
    definition: {
      immediate: true,
      deep: true,
      handler() {
        this.updateChartOptions(this.getChartOptions)
      },
    },
  },

  methods: {
    updateChartOptions() {
      this.chartOptions = this.getChartOptions
    },

    getFleetGraphData() {
      const data = {
        ...this.definition.parameterDefinition,
        values: this.getFleetGraphTailsAndValues(this.definition.fleetValues),
      }

      return data
    },

    getOrderedFilteredQuartileData(data) {
      let quartileObjects = []
      if (data && data.primaryPlots && data.primaryPlots.length) {
        quartileObjects = data.primaryPlots
      }
      if (data && data.secondaryPlots && data.secondaryPlots.length) {
        quartileObjects = quartileObjects.concat(
          data.secondaryPlots.sort((a, b) =>
            a[this.sortItem.sortField] > b[this.sortItem.sortField]
              ? this.sortItem.sortAsc
                ? 1
                : -1
              : this.sortItem.sortAsc
              ? -1
              : 1,
          ),
        )
      }

      return quartileObjects
    },

    getFleetGraphTailsAndValues(data) {
      const quartileObjects = this.getOrderedFilteredQuartileData(data)
      const mapQuartileObject = quartileObject => {
        const { index, min, pct25, pct50, pct75, max } = quartileObject
        return [index, min, pct25, pct50, pct75, max]
      }

      return quartileObjects.map((qObj, index) => {
        const updatedQObj = {
          index,
          ...qObj,
        }

        return {
          name: qObj.tail,
          data: [mapQuartileObject(updatedQObj)],
        }
      })
    },

    handleInput(id, selection) {
      if (
        selection &&
        selection.dates &&
        selection.dates.startDate &&
        selection.dates.endDate
      ) {
        const oldStartDate = this.dates.dates.startDate
        const oldEndDate = this.dates.dates.endDate
        const oldValue = this.dates.dates.value
        const newStartDate = selection.dates.startDate
        const newEndDate = selection.dates.endDate
        const newValue = selection.dates.value
        if (
          oldStartDate !== newStartDate ||
          oldEndDate !== newEndDate ||
          oldValue !== newValue
        ) {
          if (this.onDateChange) {
            this.onDateChange(selection)
          }
        }
      }
    },

    handleSort(sortItem) {
      this.sortItem = sortItem
      this.updateChartOptions(this.getChartOptions)
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-graph {
  &--chart {
    max-height: 600px;

    .fleet-graph-dates {
      width: 200px;
    }
  }

  &--filter {
    .asc-fleet-graph--sort-button {
      margin-right: 8px;
    }
  }

  &--no-data {
    min-height: 400px;
  }
}
</style>
